import { type JSX, useState } from 'react';

import { UsersApi } from 'Api/Users/UsersApi';
import { Alert } from 'Components/Alert/Alert';
import { Button } from 'Components/Buttons/Buttons';
import { Modal } from 'Components/Modal/Modal';
import { ICON_CLOSE, ICON_POSITIVE } from 'Config/Icons';
import { UserNameFormat, getUserNameFromUser } from 'Helpers/UserUtils';
import { UserResponse } from 'Models/User';

import styles from './ReactivateUserModal.module.css';

export interface ReactivateUserModalProps {
    hideModal: () => void;
    userSelectedForReactivation: UserResponse;
    usersApi: UsersApi;
    userReactivated: () => void;
}

export const ReactivateUserModal = (props: ReactivateUserModalProps): JSX.Element => {
    const [isReactivating, setIsReactivating] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | undefined>(undefined);
    const [failureMessage, setFailureMessage] = useState<string | undefined>(undefined);

    const reactivateUser = async (): Promise<void> => {
        setIsReactivating(true);
        setSuccessMessage(undefined);
        setFailureMessage(undefined);
        try {
            await props.usersApi.reactivateUser(props.userSelectedForReactivation.cognito_subject);
            setSuccessMessage('User reactivated.');
            props.userReactivated();
        } catch (error) {
            handleRequestError(error);
        } finally {
            setIsReactivating(false);
        }
    };

    const handleRequestError = (error: Error): void => {
        setFailureMessage(error.message);
        setSuccessMessage(undefined);
    };

    const hideModal = (): void => {
        if (!isReactivating) {
            props.hideModal();
        }
    };

    return (
        <Modal open header="Reactivate">
            {successMessage && <Alert type="success">{successMessage}</Alert>}
            {failureMessage && <Alert type="error">{failureMessage}</Alert>}
            <div className={styles.reactivateText}>Reactivating a user will allow the user to login to SummIT and receive notifications.</div>
            <div className={styles.reactivateText}>Are you sure you want to reactivate: {getUserNameFromUser(props.userSelectedForReactivation, UserNameFormat.FIRST_SPACE_LAST)}?</div>
            <div className={'modalFormButtonContainer'}>
                <Button variant="secondary" onClick={hideModal} disabled={isReactivating} fontAwesomeImage={ICON_CLOSE}>
                    Close
                </Button>
                <Button variant="danger" onClick={() => reactivateUser()} fontAwesomeImage={ICON_POSITIVE} loadingText={'Reactivating...'} isLoading={isReactivating} disabled={successMessage ? true : false}>
                    REACTIVATE
                </Button>
            </div>
        </Modal>
    );
};
